import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, booleanAttribute, Directive, Optional, Inject, Input, Output, Injectable, SkipSelf, Component, ViewEncapsulation, ChangeDetectionStrategy, NgModule } from '@angular/core';
import * as i3 from '@angular/cdk/a11y';
import { SPACE, ENTER } from '@angular/cdk/keycodes';
import { ReplaySubject, Subject, merge } from 'rxjs';
import { trigger, state, style, transition, animate, keyframes, query, animateChild } from '@angular/animations';
import { AnimationDurations, AnimationCurves, MatCommonModule } from '@angular/material/core';

/** @docs-private */
const _c0 = ["mat-sort-header", ""];
const _c1 = ["*"];
function MatSortHeader_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵlistener("@arrowPosition.start", function MatSortHeader_Conditional_3_Template_div_animation_arrowPosition_start_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1._disableViewStateAnimation = true);
    })("@arrowPosition.done", function MatSortHeader_Conditional_3_Template_div_animation_arrowPosition_done_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1._disableViewStateAnimation = false);
    });
    i0.ɵɵelement(1, "div", 3);
    i0.ɵɵelementStart(2, "div", 4);
    i0.ɵɵelement(3, "div", 5)(4, "div", 6)(5, "div", 7);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("@arrowOpacity", ctx_r1._getArrowViewState())("@arrowPosition", ctx_r1._getArrowViewState())("@allowChildren", ctx_r1._getArrowDirectionState());
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("@indicator", ctx_r1._getArrowDirectionState());
    i0.ɵɵadvance();
    i0.ɵɵproperty("@leftPointer", ctx_r1._getArrowDirectionState());
    i0.ɵɵadvance();
    i0.ɵɵproperty("@rightPointer", ctx_r1._getArrowDirectionState());
  }
}
function getSortDuplicateSortableIdError(id) {
  return Error(`Cannot have two MatSortables with the same id (${id}).`);
}
/** @docs-private */
function getSortHeaderNotContainedWithinSortError() {
  return Error(`MatSortHeader must be placed within a parent element with the MatSort directive.`);
}
/** @docs-private */
function getSortHeaderMissingIdError() {
  return Error(`MatSortHeader must be provided with a unique id.`);
}
/** @docs-private */
function getSortInvalidDirectionError(direction) {
  return Error(`${direction} is not a valid sort direction ('asc' or 'desc').`);
}

/** Injection token to be used to override the default options for `mat-sort`. */
const MAT_SORT_DEFAULT_OPTIONS = /*#__PURE__*/new InjectionToken('MAT_SORT_DEFAULT_OPTIONS');
/** Container for MatSortables to manage the sort state and provide default sort parameters. */
let MatSort = /*#__PURE__*/(() => {
  class MatSort {
    /** The sort direction of the currently active MatSortable. */
    get direction() {
      return this._direction;
    }
    set direction(direction) {
      if (direction && direction !== 'asc' && direction !== 'desc' && (typeof ngDevMode === 'undefined' || ngDevMode)) {
        throw getSortInvalidDirectionError(direction);
      }
      this._direction = direction;
    }
    constructor(_defaultOptions) {
      this._defaultOptions = _defaultOptions;
      this._initializedStream = new ReplaySubject(1);
      /** Collection of all registered sortables that this directive manages. */
      this.sortables = new Map();
      /** Used to notify any child components listening to state changes. */
      this._stateChanges = new Subject();
      /**
       * The direction to set when an MatSortable is initially sorted.
       * May be overridden by the MatSortable's sort start.
       */
      this.start = 'asc';
      this._direction = '';
      /** Whether the sortable is disabled. */
      this.disabled = false;
      /** Event emitted when the user changes either the active sort or sort direction. */
      this.sortChange = new EventEmitter();
      /** Emits when the paginator is initialized. */
      this.initialized = this._initializedStream;
    }
    /**
     * Register function to be used by the contained MatSortables. Adds the MatSortable to the
     * collection of MatSortables.
     */
    register(sortable) {
      if (typeof ngDevMode === 'undefined' || ngDevMode) {
        if (!sortable.id) {
          throw getSortHeaderMissingIdError();
        }
        if (this.sortables.has(sortable.id)) {
          throw getSortDuplicateSortableIdError(sortable.id);
        }
      }
      this.sortables.set(sortable.id, sortable);
    }
    /**
     * Unregister function to be used by the contained MatSortables. Removes the MatSortable from the
     * collection of contained MatSortables.
     */
    deregister(sortable) {
      this.sortables.delete(sortable.id);
    }
    /** Sets the active sort id and determines the new sort direction. */
    sort(sortable) {
      if (this.active != sortable.id) {
        this.active = sortable.id;
        this.direction = sortable.start ? sortable.start : this.start;
      } else {
        this.direction = this.getNextSortDirection(sortable);
      }
      this.sortChange.emit({
        active: this.active,
        direction: this.direction
      });
    }
    /** Returns the next sort direction of the active sortable, checking for potential overrides. */
    getNextSortDirection(sortable) {
      if (!sortable) {
        return '';
      }
      // Get the sort direction cycle with the potential sortable overrides.
      const disableClear = sortable?.disableClear ?? this.disableClear ?? !!this._defaultOptions?.disableClear;
      let sortDirectionCycle = getSortDirectionCycle(sortable.start || this.start, disableClear);
      // Get and return the next direction in the cycle
      let nextDirectionIndex = sortDirectionCycle.indexOf(this.direction) + 1;
      if (nextDirectionIndex >= sortDirectionCycle.length) {
        nextDirectionIndex = 0;
      }
      return sortDirectionCycle[nextDirectionIndex];
    }
    ngOnInit() {
      this._initializedStream.next();
    }
    ngOnChanges() {
      this._stateChanges.next();
    }
    ngOnDestroy() {
      this._stateChanges.complete();
      this._initializedStream.complete();
    }
    static {
      this.ɵfac = function MatSort_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatSort)(i0.ɵɵdirectiveInject(MAT_SORT_DEFAULT_OPTIONS, 8));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatSort,
        selectors: [["", "matSort", ""]],
        hostAttrs: [1, "mat-sort"],
        inputs: {
          active: [0, "matSortActive", "active"],
          start: [0, "matSortStart", "start"],
          direction: [0, "matSortDirection", "direction"],
          disableClear: [2, "matSortDisableClear", "disableClear", booleanAttribute],
          disabled: [2, "matSortDisabled", "disabled", booleanAttribute]
        },
        outputs: {
          sortChange: "matSortChange"
        },
        exportAs: ["matSort"],
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return MatSort;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/** Returns the sort direction cycle to use given the provided parameters of order and clear. */
function getSortDirectionCycle(start, disableClear) {
  let sortOrder = ['asc', 'desc'];
  if (start == 'desc') {
    sortOrder.reverse();
  }
  if (!disableClear) {
    sortOrder.push('');
  }
  return sortOrder;
}
const SORT_ANIMATION_TRANSITION = AnimationDurations.ENTERING + ' ' + AnimationCurves.STANDARD_CURVE;
/**
 * Animations used by MatSort.
 * @docs-private
 */
const matSortAnimations = {
  /** Animation that moves the sort indicator. */
  indicator: /*#__PURE__*/trigger('indicator', [/*#__PURE__*/state('active-asc, asc', /*#__PURE__*/style({
    transform: 'translateY(0px)'
  })),
  /*#__PURE__*/
  // 10px is the height of the sort indicator, minus the width of the pointers
  state('active-desc, desc', /*#__PURE__*/style({
    transform: 'translateY(10px)'
  })), /*#__PURE__*/transition('active-asc <=> active-desc', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION))]),
  /** Animation that rotates the left pointer of the indicator based on the sorting direction. */
  leftPointer: /*#__PURE__*/trigger('leftPointer', [/*#__PURE__*/state('active-asc, asc', /*#__PURE__*/style({
    transform: 'rotate(-45deg)'
  })), /*#__PURE__*/state('active-desc, desc', /*#__PURE__*/style({
    transform: 'rotate(45deg)'
  })), /*#__PURE__*/transition('active-asc <=> active-desc', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION))]),
  /** Animation that rotates the right pointer of the indicator based on the sorting direction. */
  rightPointer: /*#__PURE__*/trigger('rightPointer', [/*#__PURE__*/state('active-asc, asc', /*#__PURE__*/style({
    transform: 'rotate(45deg)'
  })), /*#__PURE__*/state('active-desc, desc', /*#__PURE__*/style({
    transform: 'rotate(-45deg)'
  })), /*#__PURE__*/transition('active-asc <=> active-desc', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION))]),
  /** Animation that controls the arrow opacity. */
  arrowOpacity: /*#__PURE__*/trigger('arrowOpacity', [/*#__PURE__*/state('desc-to-active, asc-to-active, active', /*#__PURE__*/style({
    opacity: 1
  })), /*#__PURE__*/state('desc-to-hint, asc-to-hint, hint', /*#__PURE__*/style({
    opacity: 0.54
  })), /*#__PURE__*/state('hint-to-desc, active-to-desc, desc, hint-to-asc, active-to-asc, asc, void', /*#__PURE__*/style({
    opacity: 0
  })),
  /*#__PURE__*/
  // Transition between all states except for immediate transitions
  transition('* => asc, * => desc, * => active, * => hint, * => void', /*#__PURE__*/animate('0ms')), /*#__PURE__*/transition('* <=> *', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION))]),
  /**
   * Animation for the translation of the arrow as a whole. States are separated into two
   * groups: ones with animations and others that are immediate. Immediate states are asc, desc,
   * peek, and active. The other states define a specific animation (source-to-destination)
   * and are determined as a function of their prev user-perceived state and what the next state
   * should be.
   */
  arrowPosition: /*#__PURE__*/trigger('arrowPosition', [
  /*#__PURE__*/
  // Hidden Above => Hint Center
  transition('* => desc-to-hint, * => desc-to-active', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION, /*#__PURE__*/keyframes([/*#__PURE__*/style({
    transform: 'translateY(-25%)'
  }), /*#__PURE__*/style({
    transform: 'translateY(0)'
  })]))),
  /*#__PURE__*/
  // Hint Center => Hidden Below
  transition('* => hint-to-desc, * => active-to-desc', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION, /*#__PURE__*/keyframes([/*#__PURE__*/style({
    transform: 'translateY(0)'
  }), /*#__PURE__*/style({
    transform: 'translateY(25%)'
  })]))),
  /*#__PURE__*/
  // Hidden Below => Hint Center
  transition('* => asc-to-hint, * => asc-to-active', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION, /*#__PURE__*/keyframes([/*#__PURE__*/style({
    transform: 'translateY(25%)'
  }), /*#__PURE__*/style({
    transform: 'translateY(0)'
  })]))),
  /*#__PURE__*/
  // Hint Center => Hidden Above
  transition('* => hint-to-asc, * => active-to-asc', /*#__PURE__*/animate(SORT_ANIMATION_TRANSITION, /*#__PURE__*/keyframes([/*#__PURE__*/style({
    transform: 'translateY(0)'
  }), /*#__PURE__*/style({
    transform: 'translateY(-25%)'
  })]))), /*#__PURE__*/state('desc-to-hint, asc-to-hint, hint, desc-to-active, asc-to-active, active', /*#__PURE__*/style({
    transform: 'translateY(0)'
  })), /*#__PURE__*/state('hint-to-desc, active-to-desc, desc', /*#__PURE__*/style({
    transform: 'translateY(-25%)'
  })), /*#__PURE__*/state('hint-to-asc, active-to-asc, asc', /*#__PURE__*/style({
    transform: 'translateY(25%)'
  }))]),
  /** Necessary trigger that calls animate on children animations. */
  allowChildren: /*#__PURE__*/trigger('allowChildren', [/*#__PURE__*/transition('* <=> *', [/*#__PURE__*/query('@*', /*#__PURE__*/animateChild(), {
    optional: true
  })])])
};

/**
 * To modify the labels and text displayed, create a new instance of MatSortHeaderIntl and
 * include it in a custom provider.
 */
let MatSortHeaderIntl = /*#__PURE__*/(() => {
  class MatSortHeaderIntl {
    constructor() {
      /**
       * Stream that emits whenever the labels here are changed. Use this to notify
       * components if the labels have changed after initialization.
       */
      this.changes = new Subject();
    }
    static {
      this.ɵfac = function MatSortHeaderIntl_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatSortHeaderIntl)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MatSortHeaderIntl,
        factory: MatSortHeaderIntl.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MatSortHeaderIntl;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/** @docs-private */
function MAT_SORT_HEADER_INTL_PROVIDER_FACTORY(parentIntl) {
  return parentIntl || new MatSortHeaderIntl();
}
/** @docs-private */
const MAT_SORT_HEADER_INTL_PROVIDER = {
  // If there is already an MatSortHeaderIntl available, use that. Otherwise, provide a new one.
  provide: MatSortHeaderIntl,
  deps: [[/*#__PURE__*/new Optional(), /*#__PURE__*/new SkipSelf(), MatSortHeaderIntl]],
  useFactory: MAT_SORT_HEADER_INTL_PROVIDER_FACTORY
};

/**
 * Applies sorting behavior (click to change sort) and styles to an element, including an
 * arrow to display the current sort direction.
 *
 * Must be provided with an id and contained within a parent MatSort directive.
 *
 * If used on header cells in a CdkTable, it will automatically default its id from its containing
 * column definition.
 */
let MatSortHeader = /*#__PURE__*/(() => {
  class MatSortHeader {
    /**
     * Description applied to MatSortHeader's button element with aria-describedby. This text should
     * describe the action that will occur when the user clicks the sort header.
     */
    get sortActionDescription() {
      return this._sortActionDescription;
    }
    set sortActionDescription(value) {
      this._updateSortActionDescription(value);
    }
    constructor(
    /**
     * @deprecated `_intl` parameter isn't being used anymore and it'll be removed.
     * @breaking-change 13.0.0
     */
    _intl, _changeDetectorRef,
    // `MatSort` is not optionally injected, but just asserted manually w/ better error.
    // tslint:disable-next-line: lightweight-tokens
    _sort, _columnDef, _focusMonitor, _elementRef, /** @breaking-change 14.0.0 _ariaDescriber will be required. */
    _ariaDescriber, defaultOptions) {
      this._intl = _intl;
      this._changeDetectorRef = _changeDetectorRef;
      this._sort = _sort;
      this._columnDef = _columnDef;
      this._focusMonitor = _focusMonitor;
      this._elementRef = _elementRef;
      this._ariaDescriber = _ariaDescriber;
      /**
       * Flag set to true when the indicator should be displayed while the sort is not active. Used to
       * provide an affordance that the header is sortable by showing on focus and hover.
       */
      this._showIndicatorHint = false;
      /**
       * The view transition state of the arrow (translation/ opacity) - indicates its `from` and `to`
       * position through the animation. If animations are currently disabled, the fromState is removed
       * so that there is no animation displayed.
       */
      this._viewState = {};
      /** The direction the arrow should be facing according to the current state. */
      this._arrowDirection = '';
      /**
       * Whether the view state animation should show the transition between the `from` and `to` states.
       */
      this._disableViewStateAnimation = false;
      /** Sets the position of the arrow that displays when sorted. */
      this.arrowPosition = 'after';
      /** whether the sort header is disabled. */
      this.disabled = false;
      // Default the action description to "Sort" because it's better than nothing.
      // Without a description, the button's label comes from the sort header text content,
      // which doesn't give any indication that it performs a sorting operation.
      this._sortActionDescription = 'Sort';
      // Note that we use a string token for the `_columnDef`, because the value is provided both by
      // `material/table` and `cdk/table` and we can't have the CDK depending on Material,
      // and we want to avoid having the sort header depending on the CDK table because
      // of this single reference.
      if (!_sort && (typeof ngDevMode === 'undefined' || ngDevMode)) {
        throw getSortHeaderNotContainedWithinSortError();
      }
      if (defaultOptions?.arrowPosition) {
        this.arrowPosition = defaultOptions?.arrowPosition;
      }
      this._handleStateChanges();
    }
    ngOnInit() {
      if (!this.id && this._columnDef) {
        this.id = this._columnDef.name;
      }
      // Initialize the direction of the arrow and set the view state to be immediately that state.
      this._updateArrowDirection();
      this._setAnimationTransitionState({
        toState: this._isSorted() ? 'active' : this._arrowDirection
      });
      this._sort.register(this);
      this._sortButton = this._elementRef.nativeElement.querySelector('.mat-sort-header-container');
      this._updateSortActionDescription(this._sortActionDescription);
    }
    ngAfterViewInit() {
      // We use the focus monitor because we also want to style
      // things differently based on the focus origin.
      this._focusMonitor.monitor(this._elementRef, true).subscribe(origin => {
        const newState = !!origin;
        if (newState !== this._showIndicatorHint) {
          this._setIndicatorHintVisible(newState);
          this._changeDetectorRef.markForCheck();
        }
      });
    }
    ngOnDestroy() {
      this._focusMonitor.stopMonitoring(this._elementRef);
      this._sort.deregister(this);
      this._rerenderSubscription.unsubscribe();
      if (this._sortButton) {
        this._ariaDescriber?.removeDescription(this._sortButton, this._sortActionDescription);
      }
    }
    /**
     * Sets the "hint" state such that the arrow will be semi-transparently displayed as a hint to the
     * user showing what the active sort will become. If set to false, the arrow will fade away.
     */
    _setIndicatorHintVisible(visible) {
      // No-op if the sort header is disabled - should not make the hint visible.
      if (this._isDisabled() && visible) {
        return;
      }
      this._showIndicatorHint = visible;
      if (!this._isSorted()) {
        this._updateArrowDirection();
        if (this._showIndicatorHint) {
          this._setAnimationTransitionState({
            fromState: this._arrowDirection,
            toState: 'hint'
          });
        } else {
          this._setAnimationTransitionState({
            fromState: 'hint',
            toState: this._arrowDirection
          });
        }
      }
    }
    /**
     * Sets the animation transition view state for the arrow's position and opacity. If the
     * `disableViewStateAnimation` flag is set to true, the `fromState` will be ignored so that
     * no animation appears.
     */
    _setAnimationTransitionState(viewState) {
      this._viewState = viewState || {};
      // If the animation for arrow position state (opacity/translation) should be disabled,
      // remove the fromState so that it jumps right to the toState.
      if (this._disableViewStateAnimation) {
        this._viewState = {
          toState: viewState.toState
        };
      }
    }
    /** Triggers the sort on this sort header and removes the indicator hint. */
    _toggleOnInteraction() {
      this._sort.sort(this);
      // Do not show the animation if the header was already shown in the right position.
      if (this._viewState.toState === 'hint' || this._viewState.toState === 'active') {
        this._disableViewStateAnimation = true;
      }
    }
    _handleClick() {
      if (!this._isDisabled()) {
        this._sort.sort(this);
      }
    }
    _handleKeydown(event) {
      if (!this._isDisabled() && (event.keyCode === SPACE || event.keyCode === ENTER)) {
        event.preventDefault();
        this._toggleOnInteraction();
      }
    }
    /** Whether this MatSortHeader is currently sorted in either ascending or descending order. */
    _isSorted() {
      return this._sort.active == this.id && (this._sort.direction === 'asc' || this._sort.direction === 'desc');
    }
    /** Returns the animation state for the arrow direction (indicator and pointers). */
    _getArrowDirectionState() {
      return `${this._isSorted() ? 'active-' : ''}${this._arrowDirection}`;
    }
    /** Returns the arrow position state (opacity, translation). */
    _getArrowViewState() {
      const fromState = this._viewState.fromState;
      return (fromState ? `${fromState}-to-` : '') + this._viewState.toState;
    }
    /**
     * Updates the direction the arrow should be pointing. If it is not sorted, the arrow should be
     * facing the start direction. Otherwise if it is sorted, the arrow should point in the currently
     * active sorted direction. The reason this is updated through a function is because the direction
     * should only be changed at specific times - when deactivated but the hint is displayed and when
     * the sort is active and the direction changes. Otherwise the arrow's direction should linger
     * in cases such as the sort becoming deactivated but we want to animate the arrow away while
     * preserving its direction, even though the next sort direction is actually different and should
     * only be changed once the arrow displays again (hint or activation).
     */
    _updateArrowDirection() {
      this._arrowDirection = this._isSorted() ? this._sort.direction : this.start || this._sort.start;
    }
    _isDisabled() {
      return this._sort.disabled || this.disabled;
    }
    /**
     * Gets the aria-sort attribute that should be applied to this sort header. If this header
     * is not sorted, returns null so that the attribute is removed from the host element. Aria spec
     * says that the aria-sort property should only be present on one header at a time, so removing
     * ensures this is true.
     */
    _getAriaSortAttribute() {
      if (!this._isSorted()) {
        return 'none';
      }
      return this._sort.direction == 'asc' ? 'ascending' : 'descending';
    }
    /** Whether the arrow inside the sort header should be rendered. */
    _renderArrow() {
      return !this._isDisabled() || this._isSorted();
    }
    _updateSortActionDescription(newDescription) {
      // We use AriaDescriber for the sort button instead of setting an `aria-label` because some
      // screen readers (notably VoiceOver) will read both the column header *and* the button's label
      // for every *cell* in the table, creating a lot of unnecessary noise.
      // If _sortButton is undefined, the component hasn't been initialized yet so there's
      // nothing to update in the DOM.
      if (this._sortButton) {
        // removeDescription will no-op if there is no existing message.
        // TODO(jelbourn): remove optional chaining when AriaDescriber is required.
        this._ariaDescriber?.removeDescription(this._sortButton, this._sortActionDescription);
        this._ariaDescriber?.describe(this._sortButton, newDescription);
      }
      this._sortActionDescription = newDescription;
    }
    /** Handles changes in the sorting state. */
    _handleStateChanges() {
      this._rerenderSubscription = merge(this._sort.sortChange, this._sort._stateChanges, this._intl.changes).subscribe(() => {
        if (this._isSorted()) {
          this._updateArrowDirection();
          // Do not show the animation if the header was already shown in the right position.
          if (this._viewState.toState === 'hint' || this._viewState.toState === 'active') {
            this._disableViewStateAnimation = true;
          }
          this._setAnimationTransitionState({
            fromState: this._arrowDirection,
            toState: 'active'
          });
          this._showIndicatorHint = false;
        }
        // If this header was recently active and now no longer sorted, animate away the arrow.
        if (!this._isSorted() && this._viewState && this._viewState.toState === 'active') {
          this._disableViewStateAnimation = false;
          this._setAnimationTransitionState({
            fromState: 'active',
            toState: this._arrowDirection
          });
        }
        this._changeDetectorRef.markForCheck();
      });
    }
    static {
      this.ɵfac = function MatSortHeader_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatSortHeader)(i0.ɵɵdirectiveInject(MatSortHeaderIntl), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(MatSort, 8), i0.ɵɵdirectiveInject('MAT_SORT_HEADER_COLUMN_DEF', 8), i0.ɵɵdirectiveInject(i3.FocusMonitor), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i3.AriaDescriber, 8), i0.ɵɵdirectiveInject(MAT_SORT_DEFAULT_OPTIONS, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MatSortHeader,
        selectors: [["", "mat-sort-header", ""]],
        hostAttrs: [1, "mat-sort-header"],
        hostVars: 3,
        hostBindings: function MatSortHeader_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function MatSortHeader_click_HostBindingHandler() {
              return ctx._handleClick();
            })("keydown", function MatSortHeader_keydown_HostBindingHandler($event) {
              return ctx._handleKeydown($event);
            })("mouseenter", function MatSortHeader_mouseenter_HostBindingHandler() {
              return ctx._setIndicatorHintVisible(true);
            })("mouseleave", function MatSortHeader_mouseleave_HostBindingHandler() {
              return ctx._setIndicatorHintVisible(false);
            });
          }
          if (rf & 2) {
            i0.ɵɵattribute("aria-sort", ctx._getAriaSortAttribute());
            i0.ɵɵclassProp("mat-sort-header-disabled", ctx._isDisabled());
          }
        },
        inputs: {
          id: [0, "mat-sort-header", "id"],
          arrowPosition: "arrowPosition",
          start: "start",
          disabled: [2, "disabled", "disabled", booleanAttribute],
          sortActionDescription: "sortActionDescription",
          disableClear: [2, "disableClear", "disableClear", booleanAttribute]
        },
        exportAs: ["matSortHeader"],
        attrs: _c0,
        ngContentSelectors: _c1,
        decls: 4,
        vars: 7,
        consts: [[1, "mat-sort-header-container", "mat-focus-indicator"], [1, "mat-sort-header-content"], [1, "mat-sort-header-arrow"], [1, "mat-sort-header-stem"], [1, "mat-sort-header-indicator"], [1, "mat-sort-header-pointer-left"], [1, "mat-sort-header-pointer-right"], [1, "mat-sort-header-pointer-middle"]],
        template: function MatSortHeader_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(3, MatSortHeader_Conditional_3_Template, 6, 6, "div", 2);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵclassProp("mat-sort-header-sorted", ctx._isSorted())("mat-sort-header-position-before", ctx.arrowPosition === "before");
            i0.ɵɵattribute("tabindex", ctx._isDisabled() ? null : 0)("role", ctx._isDisabled() ? null : "button");
            i0.ɵɵadvance(3);
            i0.ɵɵconditional(ctx._renderArrow() ? 3 : -1);
          }
        },
        styles: [".mat-sort-header-container{display:flex;cursor:pointer;align-items:center;letter-spacing:normal;outline:0}[mat-sort-header].cdk-keyboard-focused .mat-sort-header-container,[mat-sort-header].cdk-program-focused .mat-sort-header-container{border-bottom:solid 1px currentColor}.mat-sort-header-disabled .mat-sort-header-container{cursor:default}.mat-sort-header-container::before{margin:calc(calc(var(--mat-focus-indicator-border-width, 3px) + 2px)*-1)}.mat-sort-header-content{text-align:center;display:flex;align-items:center}.mat-sort-header-position-before{flex-direction:row-reverse}.mat-sort-header-arrow{height:12px;width:12px;min-width:12px;position:relative;display:flex;color:var(--mat-sort-arrow-color);opacity:0}.mat-sort-header-arrow,[dir=rtl] .mat-sort-header-position-before .mat-sort-header-arrow{margin:0 0 0 6px}.mat-sort-header-position-before .mat-sort-header-arrow,[dir=rtl] .mat-sort-header-arrow{margin:0 6px 0 0}.mat-sort-header-stem{background:currentColor;height:10px;width:2px;margin:auto;display:flex;align-items:center}.cdk-high-contrast-active .mat-sort-header-stem{width:0;border-left:solid 2px}.mat-sort-header-indicator{width:100%;height:2px;display:flex;align-items:center;position:absolute;top:0;left:0}.mat-sort-header-pointer-middle{margin:auto;height:2px;width:2px;background:currentColor;transform:rotate(45deg)}.cdk-high-contrast-active .mat-sort-header-pointer-middle{width:0;height:0;border-top:solid 2px;border-left:solid 2px}.mat-sort-header-pointer-left,.mat-sort-header-pointer-right{background:currentColor;width:6px;height:2px;position:absolute;top:0}.cdk-high-contrast-active .mat-sort-header-pointer-left,.cdk-high-contrast-active .mat-sort-header-pointer-right{width:0;height:0;border-left:solid 6px;border-top:solid 2px}.mat-sort-header-pointer-left{transform-origin:right;left:0}.mat-sort-header-pointer-right{transform-origin:left;right:0}"],
        encapsulation: 2,
        data: {
          animation: [matSortAnimations.indicator, matSortAnimations.leftPointer, matSortAnimations.rightPointer, matSortAnimations.arrowOpacity, matSortAnimations.arrowPosition, matSortAnimations.allowChildren]
        },
        changeDetection: 0
      });
    }
  }
  return MatSortHeader;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MatSortModule = /*#__PURE__*/(() => {
  class MatSortModule {
    static {
      this.ɵfac = function MatSortModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatSortModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: MatSortModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [MAT_SORT_HEADER_INTL_PROVIDER],
        imports: [MatCommonModule]
      });
    }
  }
  return MatSortModule;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { MAT_SORT_DEFAULT_OPTIONS, MAT_SORT_HEADER_INTL_PROVIDER, MAT_SORT_HEADER_INTL_PROVIDER_FACTORY, MatSort, MatSortHeader, MatSortHeaderIntl, MatSortModule, matSortAnimations };
